import { formatDateRange } from '@longy/common'
import { Link } from '@tanstack/react-router'
import { Calendar, ChevronRight, MapPin } from 'lucide-react'

import { api } from '../../core/trpc'
import { useLocale } from '../../hooks/useLocale'
import { useWebContent } from '../../hooks/useWebContent'
import { richTextListClassName } from '../../utils/richText'
import { EventImage } from '../events/EventImage'
import { FullyBookedBadge } from '../events/FullyBookedBadge'

export const EventsSection = () => {
    const [events] = api.event.getPublicEvents.useSuspenseQuery()
    const { getLocalizedValue } = useLocale()
    const { getText } = useWebContent('EVENTS')

    return (
        <section id="events" className="bg-white py-24">
            <div className="mx-auto w-full max-w-[1280px] px-6">
                <div className="mb-16 flex flex-col justify-between gap-4 md:flex-row md:items-end">
                    <div>
                        <h2 className="mb-4 text-3xl font-bold text-brand-dark md:text-4xl">
                            {getText('heading')}
                        </h2>
                        <div
                            dangerouslySetInnerHTML={{ __html: getText('subheading') }}
                            className={`max-w-xl text-brand-gray ${richTextListClassName}`}
                        />
                    </div>
                    <a
                        href="/events"
                        className="flex items-center gap-1 font-bold text-primary transition-colors hover:text-brand-yellow"
                    >
                        {getText('viewAllLink')} <ChevronRight className="h-4 w-4" />
                    </a>
                </div>

                <div className="grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-3">
                    {events.map((event) => (
                        <div
                            key={event.id}
                            className="group flex flex-col overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl"
                        >
                            <div className="relative">
                                <EventImage
                                    alt={event.name ? getLocalizedValue(event.name) : 'Esemény'}
                                    className="aspect-[4/3]"
                                    fileId={event.images?.[0]?.file?.id}
                                />
                                <FullyBookedBadge
                                    className="absolute right-4 top-4"
                                    fullyBooked={event.fullyBooked}
                                />
                                {event.categoryName && (
                                    <div className="absolute left-4 top-4 rounded-full bg-brand-yellow px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-brand-dark shadow-sm">
                                        {getLocalizedValue(event.categoryName)}
                                    </div>
                                )}
                            </div>
                            <div className="flex flex-1 flex-col p-8">
                                <h3 className="mb-4 text-xl font-bold text-brand-dark">
                                    {event.name ? getLocalizedValue(event.name) : 'Esemény'}
                                </h3>
                                <div className="mb-8 space-y-2 text-sm leading-relaxed text-brand-gray">
                                    {event.startDate && (
                                        <div className="flex items-center gap-2">
                                            <Calendar className="h-4 w-4 text-primary" />
                                            {formatDateRange(event.startDate, event.endDate)}
                                        </div>
                                    )}
                                    {event.locationName &&
                                        getLocalizedValue(event.locationName) && (
                                            <div className="flex items-center gap-2">
                                                <MapPin className="h-4 w-4 text-primary" />
                                                {getLocalizedValue(event.locationName)}
                                            </div>
                                        )}
                                </div>
                                <Link
                                    params={{ eventId: event.id }}
                                    to="/events/$eventId"
                                    className="mt-auto flex w-full items-center justify-center gap-2 rounded-full border-2 border-primary py-3.5 font-bold text-primary transition-all hover:bg-primary hover:text-white"
                                >
                                    {getText('buttonText')}
                                </Link>
                            </div>
                        </div>
                    ))}
                </div>

                {events.length === 0 && (
                    <div className="py-16 text-center">
                        <p className="text-lg text-brand-gray">{getText('emptyState')}</p>
                    </div>
                )}
            </div>
        </section>
    )
}
